<template>
  <div class="mobile-nav">
    <nav class="mobile-nav__links" aria-label="主导航">
      <router-link to="/blogs" class="mobile-nav__link" active-class="mobile-nav__link--active">
        编辑推荐
      </router-link>
      <router-link to="/exames" class="mobile-nav__link" active-class="mobile-nav__link--active">
        测试
      </router-link>
      <router-link to="/knowledge/courses" class="mobile-nav__link" active-class="mobile-nav__link--active">
        课程
      </router-link>
      <el-tooltip
        :disabled="!referralBindPending"
        content="您尚未绑定礼券。绑定后可获得相应礼遇。"
        placement="bottom"
      >
        <router-link
          to="/referral/bind-coupon"
          class="mobile-nav__link mobile-nav__link--referral"
          :class="{ 'mobile-nav__link--active': isReferralNavActive, 'mobile-nav__link--pending': referralBindPending }"
        >
          <span v-if="referralBindPending" class="mobile-nav__dot" aria-hidden="true" />
          推荐券
        </router-link>
      </el-tooltip>

      <el-dropdown trigger="click" class="mobile-nav__dropdown" @command="onFriendMenuCommand">
        <button
          type="button"
          class="mobile-nav__link mobile-nav__link--dropdown"
          :class="{ 'mobile-nav__link--active': isFriendMenuActive }"
        >
          朋友
          <span class="mobile-nav__caret" aria-hidden="true">▾</span>
        </button>
        <template #dropdown>
          <el-dropdown-menu class="mobile-nav__friend-menu">
            <el-dropdown-item
              v-for="link in friendLinks"
              :key="link.to"
              :command="link.to"
            >
              {{ link.label }}
            </el-dropdown-item>
          </el-dropdown-menu>
        </template>
      </el-dropdown>

      <el-dropdown trigger="click" class="mobile-nav__dropdown" @command="onOtherMenuCommand">
        <button
          type="button"
          class="mobile-nav__link mobile-nav__link--dropdown"
          :class="{ 'mobile-nav__link--active': isOtherMenuActive }"
        >
          其他
          <span class="mobile-nav__caret" aria-hidden="true">▾</span>
        </button>
        <template #dropdown>
          <el-dropdown-menu class="mobile-nav__other-menu">
            <el-dropdown-item
              v-for="link in otherLinks"
              :key="link.to"
              :command="link.to"
            >
              {{ link.label }}
            </el-dropdown-item>
          </el-dropdown-menu>
        </template>
      </el-dropdown>
    </nav>

    <div class="mobile-nav__actions">
      <template v-if="isLoggedIn">
        <el-dropdown trigger="click" class="mobile-nav__user-dropdown" @command="onUserCommand">
          <button type="button" class="mobile-nav__user">
            <el-badge :value="unreadMessageCount" :hidden="!unreadMessageCount" :max="99">
              <el-avatar :size="28" :src="userInfo.avatar">
                {{ userInfo.nickname?.[0] || userInfo.username?.[0] || '我' }}
              </el-avatar>
            </el-badge>
          </button>
          <template #dropdown>
            <el-dropdown-menu>
              <el-dropdown-item command="/user/info">个人朋友信息</el-dropdown-item>
              <el-dropdown-item command="/user/password">{{ accountSecurityLabel }}</el-dropdown-item>
              <el-dropdown-item command="/knowledge/progress">学习进度</el-dropdown-item>
              <el-dropdown-item command="/creator/works">我的创作</el-dropdown-item>
              <el-dropdown-item divided command="/my">我的</el-dropdown-item>
              <el-dropdown-item command="/user/messages">
                站内信
                <el-badge v-if="unreadMessageCount" :value="unreadMessageCount" :max="99" />
              </el-dropdown-item>
              <el-dropdown-item command="/real-name-verification">实名认证</el-dropdown-item>
              <el-dropdown-item command="/withdraw/apply">提现申请</el-dropdown-item>
              <el-dropdown-item command="/referral/earnings">推荐收益</el-dropdown-item>
              <el-dropdown-item command="/referral/partner-apply">申请合伙人</el-dropdown-item>
              <el-dropdown-item command="/transactions">账单明细</el-dropdown-item>
              <el-dropdown-item v-if="isSystemAdmin" divided command="/admin">管理员后台</el-dropdown-item>
              <el-dropdown-item divided command="logout">退出登录</el-dropdown-item>
            </el-dropdown-menu>
          </template>
        </el-dropdown>
      </template>
      <button
        v-else
        type="button"
        class="mobile-nav__login-btn"
        @click="goToLogin($router, $route.fullPath)"
      >
        登录
      </button>
    </div>
  </div>
</template>

<script>
import { goToLogin } from '@/utils/loginFlow'
import { accountSecurityMenuLabel } from '@/utils/accountSetup'
import { MOBILE_OTHER_LINKS, MOBILE_FRIEND_LINKS } from '@/config/siteFooter'

const FRIEND_MENU_PATHS = MOBILE_FRIEND_LINKS.map((l) => l.to)
const OTHER_MENU_PATHS = MOBILE_OTHER_LINKS.map((l) => l.to)
/** 顶栏已单独展示的入口，不重复高亮「其他」 */
const MOBILE_PRIMARY_NAV_PATHS = ['/blogs', '/exames', '/knowledge/courses', '/referral/bind-coupon']

function pathMatchesMenu(path, menuPath) {
  return path === menuPath || (menuPath !== '/' && path.startsWith(`${menuPath}/`))
}

export default {
  name: 'MobileNavBar',
  props: {
    isLoggedIn: { type: Boolean, default: false },
    isSystemAdmin: { type: Boolean, default: false },
    referralBindPending: { type: Boolean, default: false },
    unreadMessageCount: { type: Number, default: 0 },
    userInfo: {
      type: Object,
      default: () => ({ username: '', nickname: '', avatar: '' })
    }
  },
  emits: ['logout'],
  data() {
    return {
      friendLinks: MOBILE_FRIEND_LINKS,
      otherLinks: MOBILE_OTHER_LINKS
    }
  },
  computed: {
    accountSecurityLabel() {
      return accountSecurityMenuLabel(this.userInfo)
    },
    isReferralNavActive() {
      return pathMatchesMenu(this.$route.path, '/referral/bind-coupon')
    },
    isFriendMenuActive() {
      const path = this.$route.path
      return FRIEND_MENU_PATHS.some((p) => pathMatchesMenu(path, p))
    },
    isOtherMenuActive() {
      const path = this.$route.path
      if (MOBILE_PRIMARY_NAV_PATHS.some((p) => pathMatchesMenu(path, p))) {
        return false
      }
      return OTHER_MENU_PATHS.some((p) => pathMatchesMenu(path, p))
    }
  },
  methods: {
    goToLogin,
    onFriendMenuCommand(path) {
      if (path) this.$router.push(path)
    },
    onOtherMenuCommand(path) {
      if (path) this.$router.push(path)
    },
    onUserCommand(command) {
      if (command === 'logout') {
        this.$emit('logout')
        return
      }
      this.$router.push(command)
    }
  }
}
</script>

<style scoped>
.mobile-nav {
  display: flex;
  align-items: center;
  gap: 8px;
  width: 100%;
  min-height: 52px;
  padding: 8px 8px 8px 12px;
  box-sizing: border-box;
  background: #fff;
  border-bottom: 1px solid #ebeef5;
}

.mobile-nav__links {
  flex: 1;
  display: flex;
  align-items: center;
  justify-content: flex-start;
  gap: 2px;
  min-width: 0;
  overflow-x: auto;
  -webkit-overflow-scrolling: touch;
  scrollbar-width: none;
}

.mobile-nav__links::-webkit-scrollbar {
  display: none;
}

.mobile-nav__link {
  flex-shrink: 0;
  padding: 6px 8px;
  font-size: 14px;
  color: #606266;
  text-decoration: none;
  border-radius: 6px;
  white-space: nowrap;
  border: none;
  background: transparent;
  cursor: pointer;
  line-height: 1.4;
}

.mobile-nav__link--dropdown {
  display: inline-flex;
  align-items: center;
  gap: 2px;
}

.mobile-nav__caret {
  font-size: 10px;
  opacity: 0.7;
}

.mobile-nav__link--active {
  color: #42b983;
  font-weight: 600;
  background: #eef9f3;
}

.mobile-nav__dropdown {
  flex-shrink: 0;
}

.mobile-nav__link--pending {
  position: relative;
}

.mobile-nav__dot {
  position: absolute;
  top: 4px;
  right: 2px;
  width: 7px;
  height: 7px;
  border-radius: 50%;
  background: #e6a23c;
  box-shadow: 0 0 0 1px #fff;
}

.mobile-nav__link--referral {
  position: relative;
}

.mobile-nav__actions {
  flex-shrink: 0;
}

.mobile-nav__login-btn {
  padding: 7px 12px;
  font-size: 14px;
  font-weight: 600;
  color: #fff;
  background: linear-gradient(135deg, #42b983 0%, #35946a 100%);
  border: none;
  border-radius: 8px;
  box-shadow: 0 2px 6px rgba(66, 185, 131, 0.35);
  cursor: pointer;
}

.mobile-nav__login-btn:active {
  opacity: 0.9;
}

.mobile-nav__user-dropdown {
  flex-shrink: 0;
}

.mobile-nav__user {
  padding: 0;
  border: none;
  background: transparent;
  cursor: pointer;
  line-height: 0;
}
</style>
